/**
 * SmartProvinceSelector.jsx
 * Tiered province selector: top 4 provinces as large cards,
 * remaining provinces in a collapsible "Más provincias" row.
 */
import React, { useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProvinceFlag } from './ProvinceFlags';

/* ── Province tiers ── */
const TOP_PROVINCES = [
  { code: 'ON', label: 'Ontario', pct: '~39%' },
  { code: 'QC', label: 'Quebec', pct: '~23%' },
  { code: 'BC', label: 'British Columbia', pct: '~15%' },
  { code: 'AB', label: 'Alberta', pct: '~12%' },
];

const OTHER_PROVINCES = [
  { code: 'MB', label: 'Manitoba' },
  { code: 'SK', label: 'Saskatchewan' },
  { code: 'NS', label: 'Nova Scotia' },
  { code: 'NB', label: 'New Brunswick' },
  { code: 'NL', label: 'Newfoundland and Labrador' },
  { code: 'PE', label: 'Prince Edward Island' },
  { code: 'NT', label: 'Northwest Territories' },
  { code: 'YT', label: 'Yukon' },
  { code: 'NU', label: 'Nunavut' },
];

const SmartProvinceSelector = ({
  value,
  onSelect,
  showUnsure = false,
  unsureLabel = 'No sé / no he decidido',
}) => {
  const [expanded, setExpanded] = useState(false);

  // If user selected one from "other", auto-expand
  const isOtherSelected = OTHER_PROVINCES.some((p) => p.code === value);
  const showExpanded = expanded || isOtherSelected;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* ── Top 4 as cards ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}
      >
        {TOP_PROVINCES.map((p) => {
          const selected = value === p.code;
          return (
            <Box
              key={p.code}
              onClick={() => onSelect(p.code)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(p.code);
                }
              }}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                border: selected ? '1.5px solid #1E3A5F' : '1px solid #EEF0F3',
                bgcolor: selected ? '#F7F9FC' : '#FFFFFF',
                boxShadow: selected
                  ? '0 2px 8px rgba(30,58,95,0.14)'
                  : '0 1px 4px rgba(0,0,0,0.04)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#1E3A5F',
                  bgcolor: '#F7F9FC',
                  boxShadow: '0 3px 10px rgba(30,58,95,0.10)',
                },
                '&:focus-visible': {
                  outline: '2px solid #1E3A5F',
                  outlineOffset: 2,
                },
              }}
            >
              <ProvinceFlag code={p.code} size={20} />
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: selected ? 700 : 600,
                  color: '#1A1A1A',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {p.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#5F6B7A',
                  opacity: 0.7,
                }}
              >
                {p.pct} latinos
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* ── "Más provincias" toggle ── */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          py: 0.75,
          cursor: 'pointer',
          borderRadius: '8px',
          transition: 'all 0.15s ease',
          '&:hover': { bgcolor: '#F7F9FC' },
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#5F6B7A',
          }}
        >
          {showExpanded ? 'Menos provincias' : `Más provincias (${OTHER_PROVINCES.length})`}
        </Typography>
        {showExpanded ? (
          <ExpandLessIcon sx={{ fontSize: 18, color: '#5F6B7A' }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 18, color: '#5F6B7A' }} />
        )}
      </Box>

      {/* ── Collapsible other provinces ── */}
      <Box sx={{ display: showExpanded ? 'flex' : 'none', flexWrap: 'wrap', gap: 1 }}>
          {OTHER_PROVINCES.map((p) => (
            <Chip
              key={p.code}
              icon={<ProvinceFlag code={p.code} />}
              label={p.label}
              clickable
              onClick={() => onSelect(p.code)}
              sx={{
                height: 36,
                fontSize: '13px',
                fontWeight: value === p.code ? 700 : 500,
                px: 0.5,
                bgcolor: value === p.code ? '#1E3A5F' : '#FFFFFF',
                color: value === p.code ? '#FFFFFF' : '#1A1A1A',
                border:
                  value === p.code
                    ? '1.5px solid #1E3A5F'
                    : '1px solid #EEF0F3',
                boxShadow:
                  value === p.code
                    ? '0 2px 8px rgba(30,58,95,0.14)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                transition: 'all 0.15s ease',
                '& .MuiChip-icon': { ml: '8px', mr: '-2px' },
                '&:hover': {
                  bgcolor: value === p.code ? '#1A3152' : '#F7F9FC',
                  borderColor: '#1E3A5F',
                },
              }}
            />
          ))}
        </Box>

      {/* ── "No sé" option ── */}
      {showUnsure && (
        <Chip
          icon={<ProvinceFlag code="UNKNOWN" />}
          label={unsureLabel}
          clickable
          onClick={() => onSelect('UNKNOWN')}
          sx={{
            height: 36,
            fontSize: '13px',
            fontWeight: value === 'UNKNOWN' ? 700 : 500,
            px: 0.5,
            alignSelf: 'flex-start',
            bgcolor: value === 'UNKNOWN' ? '#1E3A5F' : '#FFFFFF',
            color: value === 'UNKNOWN' ? '#FFFFFF' : '#5F6B7A',
            border:
              value === 'UNKNOWN'
                ? '1.5px solid #1E3A5F'
                : '1px solid #EEF0F3',
            boxShadow:
              value === 'UNKNOWN'
                ? '0 2px 8px rgba(30,58,95,0.14)'
                : '0 1px 4px rgba(0,0,0,0.06)',
            transition: 'all 0.15s ease',
            '& .MuiChip-icon': { ml: '8px', mr: '-2px' },
            '&:hover': {
              bgcolor: value === 'UNKNOWN' ? '#1A3152' : '#F7F9FC',
              borderColor: '#1E3A5F',
            },
          }}
        />
      )}
    </Box>
  );
};

export default SmartProvinceSelector;
